/**
 * Admin Page - User management for administrators
 *
 * Allows admins to view, edit roles, and manage users.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  adminService,
  type UserSummary,
  type WhitelistResponse,
  type ClinicGroupSummary,
} from "../services/admin";
import { authService } from "../services/auth";
import { Router } from "@vaadin/router";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "@shoelace-style/shoelace/dist/components/checkbox/checkbox.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/tab-group/tab-group.js";
import "@shoelace-style/shoelace/dist/components/tab/tab.js";
import "@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js";
import "@shoelace-style/shoelace/dist/components/switch/switch.js";
import "../components/app-shell";

@customElement("admin-page")
export class AdminPage extends LitElement {
  @state() private users: UserSummary[] = [];
  @state() private isLoading = true;
  @state() private error = "";
  @state() private editingUser: UserSummary | null = null;
  @state() private editRoles: Set<string> = new Set();
  @state() private showDeletedUsers = false;
  @state() private searchQuery = "";
  @state() private roleFilter = "all";
  @state() private activeTab = "users";

  // Whitelist state
  @state() private whitelist: WhitelistResponse | null = null;
  @state() private whitelistLoading = false;
  @state() private newWhitelistEmail = "";
  @state() private newWhitelistNote = "";
  @state() private newWhitelistClinicGroupId = "";
  @state() private newWhitelistRole = "";

  // Clinic Groups state
  @state() private clinicGroups: ClinicGroupSummary[] = [];
  @state() private clinicsLoading = false;
  @state() private newClinicName = "";
  @state() private newClinicDescription = "";

  static styles = css`
    :host {
      display: block;
      padding: 1rem;
      max-width: 1200px;
      margin: 0 auto;
    }

    h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.5rem;
      color: #333;
    }

    .subtitle {
      color: #6c757d;
      font-size: 0.875rem;
      margin-bottom: 1.5rem;
    }

    .toolbar {
      display: flex;
      gap: 1rem;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
      align-items: center;
    }

    .search-box {
      flex: 1;
      min-width: 200px;
    }
    .role-filter {
      min-width: 150px;
    }

    .tab-content {
      padding: 1rem 0;
    }

    .whitelist-section {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .whitelist-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .whitelist-add-form {
      display: flex;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
    }

    .whitelist-add-form sl-input {
      flex: 1;
      min-width: 200px;
    }

    .whitelist-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 0.5rem;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .whitelist-table th,
    .whitelist-table td {
      padding: 0.75rem 1rem;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    .whitelist-table th {
      background: #f8f9fa;
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      color: #6c757d;
    }
    .users-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 0.5rem;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .users-table th,
    .users-table td {
      padding: 0.75rem 1rem;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    .users-table th {
      background: #f8f9fa;
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      color: #6c757d;
    }

    .users-table tr:hover {
      background: #f8f9fa;
    }

    .users-table tr.deleted {
      opacity: 0.5;
      background: #fff5f5;
    }

    .user-name {
      font-weight: 500;
    }

    .user-email {
      font-size: 0.875rem;
      color: #6c757d;
    }

    .roles {
      display: flex;
      gap: 0.25rem;
      flex-wrap: wrap;
    }

    .actions {
      display: flex;
      gap: 0.5rem;
    }

    .empty-state {
      text-align: center;
      padding: 3rem;
      color: #6c757d;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      padding: 3rem;
    }

    .access-denied {
      text-align: center;
      padding: 3rem;
    }

    .access-denied sl-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }

    .dialog-content {
      padding: 1rem 0;
    }

    .role-checkboxes {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin: 1rem 0;
    }

    .stats-row {
      display: flex;
      gap: 1.5rem;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
    }

    .stat-card {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem 1.5rem;
      min-width: 120px;
    }

    .stat-value {
      font-size: 1.5rem;
      font-weight: 600;
      color: #333;
    }

    .stat-label {
      font-size: 0.75rem;
      color: #6c757d;
    }

    @media (max-width: 768px) {
      .users-table {
        display: block;
        overflow-x: auto;
      }

      .toolbar {
        flex-direction: column;
      }

      .search-box {
        width: 100%;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();

    // Check if user is admin
    if (!authService.state.isAdmin) {
      return;
    }

    this.loadUsers();
    this.loadWhitelist();
    this.loadClinicGroups();
  }

  private async loadClinicGroups() {
    this.clinicsLoading = true;
    try {
      this.clinicGroups = await adminService.getClinicGroups();
    } catch (err) {
      console.error("Failed to load clinic groups:", err);
    } finally {
      this.clinicsLoading = false;
    }
  }

  private async loadWhitelist() {
    this.whitelistLoading = true;
    try {
      this.whitelist = await adminService.getWhitelist();
    } catch (err) {
      console.error("Failed to load whitelist:", err);
    } finally {
      this.whitelistLoading = false;
    }
  }

  private async loadUsers() {
    this.isLoading = true;
    this.error = "";

    try {
      this.users = await adminService.getUsers();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load users";
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    // Check admin access
    if (!authService.state.isAdmin) {
      return this.renderAccessDenied();
    }

    return html`
      <app-shell pageTitle="Admin">
        <h1>
          <sl-icon name="shield-lock" style="vertical-align: middle;"></sl-icon>
          Admin Panel
        </h1>
        <p class="subtitle">Manage users and system settings</p>

        ${this.error
          ? html`<sl-alert variant="danger" open>${this.error}</sl-alert>`
          : ""}

        <sl-tab-group
          @sl-tab-show=${(e: CustomEvent) => (this.activeTab = e.detail.name)}
        >
          <sl-tab
            slot="nav"
            panel="users"
            ?active=${this.activeTab === "users"}
          >
            <sl-icon name="people"></sl-icon>
            Users
          </sl-tab>
          <sl-tab
            slot="nav"
            panel="clinics"
            ?active=${this.activeTab === "clinics"}
          >
            <sl-icon name="hospital"></sl-icon>
            Clinics
          </sl-tab>
          <sl-tab
            slot="nav"
            panel="whitelist"
            ?active=${this.activeTab === "whitelist"}
          >
            <sl-icon name="shield-check"></sl-icon>
            Whitelist (Beta)
          </sl-tab>

          <sl-tab-panel name="users" class="tab-content">
            ${this.isLoading
              ? this.renderLoading()
              : html`
                  ${this.renderStats()} ${this.renderToolbar()}
                  ${this.renderUsersTable()}
                `}
          </sl-tab-panel>

          <sl-tab-panel name="clinics" class="tab-content">
            ${this.renderClinicsPanel()}
          </sl-tab-panel>

          <sl-tab-panel name="whitelist" class="tab-content">
            ${this.renderWhitelistPanel()}
          </sl-tab-panel>
        </sl-tab-group>

        ${this.renderEditDialog()}
      </app-shell>
    `;
  }

  private renderAccessDenied() {
    return html`
      <div class="access-denied">
        <sl-icon name="shield-x"></sl-icon>
        <h2>Access Denied</h2>
        <p>You don't have permission to access the admin panel.</p>
        <sl-button variant="primary" @click=${() => Router.go("/")}>
          Go to Home
        </sl-button>
      </div>
    `;
  }

  private renderLoading() {
    return html`
      <div class="spinner-container">
        <sl-spinner style="font-size: 2rem;"></sl-spinner>
      </div>
    `;
  }

  private renderStats() {
    const totalUsers = this.users.length;
    const activeUsers = this.users.filter((u) => !u.isDeleted).length;
    const adminUsers = this.users.filter((u) => u.isAdmin).length;
    const diabeticUsers = this.users.filter((u) =>
      u.roles.includes("diabetic"),
    ).length;
    const monitorUsers = this.users.filter((u) =>
      u.roles.includes("monitor"),
    ).length;
    const clinicianUsers = this.users.filter((u) =>
      u.roles.includes("clinician"),
    ).length;

    return html`
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-value">${totalUsers}</div>
          <div class="stat-label">Total Users</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${activeUsers}</div>
          <div class="stat-label">Active</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${adminUsers}</div>
          <div class="stat-label">Admins</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${diabeticUsers}</div>
          <div class="stat-label">Diabetics</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${monitorUsers}</div>
          <div class="stat-label">Monitors</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${clinicianUsers}</div>
          <div class="stat-label">Clinicians</div>
        </div>
      </div>
    `;
  }

  private renderToolbar() {
    return html`
      <div class="toolbar">
        <sl-input
          class="search-box"
          placeholder="Search users..."
          clearable
          @sl-input=${(e: Event) =>
            (this.searchQuery = (e.target as HTMLInputElement).value)}
        >
          <sl-icon slot="prefix" name="search"></sl-icon>
        </sl-input>
        <sl-select
          class="role-filter"
          value=${this.roleFilter}
          @sl-change=${(e: Event) =>
            (this.roleFilter = (e.target as HTMLSelectElement).value)}
        >
          <sl-option value="all">All Roles</sl-option>
          <sl-option value="diabetic">Diabetics</sl-option>
          <sl-option value="monitor">Monitors</sl-option>
          <sl-option value="clinician">Clinicians</sl-option>
          <sl-option value="admin">Admins</sl-option>
        </sl-select>
        <sl-checkbox
          ?checked=${this.showDeletedUsers}
          @sl-change=${(e: Event) =>
            (this.showDeletedUsers = (e.target as HTMLInputElement).checked)}
        >
          Show deleted users
        </sl-checkbox>
        <sl-button @click=${() => this.loadUsers()}>
          <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
          Refresh
        </sl-button>
      </div>
    `;
  }

  private renderUsersTable() {
    let filteredUsers = this.users;

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filteredUsers = filteredUsers.filter(
        (u) =>
          u.displayName.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query),
      );
    }

    // Filter by role
    if (this.roleFilter !== "all") {
      if (this.roleFilter === "admin") {
        filteredUsers = filteredUsers.filter((u) => u.isAdmin);
      } else {
        filteredUsers = filteredUsers.filter((u) =>
          u.roles.includes(this.roleFilter),
        );
      }
    }

    // Filter deleted users
    if (!this.showDeletedUsers) {
      filteredUsers = filteredUsers.filter((u) => !u.isDeleted);
    }

    if (filteredUsers.length === 0) {
      return html`
        <div class="empty-state">
          <p>No users found</p>
        </div>
      `;
    }

    return html`
      <table class="users-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Roles</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${filteredUsers.map((user) => this.renderUserRow(user))}
        </tbody>
      </table>
    `;
  }

  private renderUserRow(user: UserSummary) {
    const currentUserId = authService.state.userId;
    const isCurrentUser = user.userId === currentUserId;

    return html`
      <tr class="${user.isDeleted ? "deleted" : ""}">
        <td>
          <div class="user-name">
            ${user.displayName}
            ${user.isAdmin
              ? html`<sl-badge variant="warning" style="margin-left: 0.5rem;"
                  >Admin</sl-badge
                >`
              : ""}
            ${isCurrentUser
              ? html`<sl-badge variant="neutral" style="margin-left: 0.5rem;"
                  >You</sl-badge
                >`
              : ""}
          </div>
          <div class="user-email">${user.email}</div>
        </td>
        <td>
          <div class="roles">
            ${user.roles.map(
              (role) => html`
                <sl-badge
                  variant="${role === "diabetic"
                    ? "primary"
                    : role === "monitor"
                      ? "success"
                      : "neutral"}"
                >
                  ${role}
                </sl-badge>
              `,
            )}
          </div>
        </td>
        <td>
          ${user.isDeleted
            ? html`<sl-badge variant="danger">Deleted</sl-badge>`
            : html`<sl-badge variant="success">Active</sl-badge>`}
        </td>
        <td>${new Date(user.createdAt).toLocaleDateString()}</td>
        <td>
          <div class="actions">
            <sl-button size="small" @click=${() => this.editUser(user)}>
              <sl-icon name="pencil"></sl-icon>
            </sl-button>
            ${!isCurrentUser
              ? html`
                  ${user.isDeleted
                    ? html`
                        <sl-button
                          size="small"
                          @click=${() => this.restoreUser(user)}
                        >
                          <sl-icon name="arrow-counterclockwise"></sl-icon>
                        </sl-button>
                      `
                    : html`
                        <sl-button
                          size="small"
                          variant="danger"
                          @click=${() => this.deleteUser(user)}
                        >
                          <sl-icon name="trash"></sl-icon>
                        </sl-button>
                      `}
                `
              : ""}
          </div>
        </td>
      </tr>
    `;
  }

  private renderEditDialog() {
    if (!this.editingUser) return "";

    const currentUserId = authService.state.userId;
    const isCurrentUser = this.editingUser.userId === currentUserId;

    return html`
      <sl-dialog
        label="Edit User - ${this.editingUser.displayName}"
        ?open=${!!this.editingUser}
        @sl-hide=${() => (this.editingUser = null)}
      >
        <div class="dialog-content">
          <p><strong>Email:</strong> ${this.editingUser.email}</p>

          <h4>Roles</h4>
          <div class="role-checkboxes">
            <sl-checkbox
              ?checked=${this.editRoles.has("diabetic")}
              @sl-change=${(e: Event) =>
                this.toggleRole(
                  "diabetic",
                  (e.target as HTMLInputElement).checked,
                )}
            >
              Diabetic (person with diabetes)
            </sl-checkbox>
            <sl-checkbox
              ?checked=${this.editRoles.has("monitor")}
              @sl-change=${(e: Event) =>
                this.toggleRole(
                  "monitor",
                  (e.target as HTMLInputElement).checked,
                )}
            >
              Monitor (family/friend)
            </sl-checkbox>
            <sl-checkbox
              ?checked=${this.editRoles.has("clinician")}
              @sl-change=${(e: Event) =>
                this.toggleRole(
                  "clinician",
                  (e.target as HTMLInputElement).checked,
                )}
            >
              Clinician (healthcare professional)
            </sl-checkbox>
          </div>

          ${!isCurrentUser
            ? html`
                <h4>Admin Status</h4>
                <sl-checkbox
                  ?checked=${this.editingUser.isAdmin}
                  @sl-change=${(e: Event) =>
                    this.toggleAdmin((e.target as HTMLInputElement).checked)}
                >
                  Administrator
                </sl-checkbox>
              `
            : ""}
        </div>

        <sl-button slot="footer" @click=${() => (this.editingUser = null)}>
          Cancel
        </sl-button>
        <sl-button
          slot="footer"
          variant="primary"
          @click=${() => this.saveUser()}
        >
          Save Changes
        </sl-button>
      </sl-dialog>
    `;
  }

  private editUser(user: UserSummary) {
    this.editingUser = user;
    this.editRoles = new Set(user.roles);
  }

  // =========================================================================
  // Clinics Panel
  // =========================================================================

  private renderClinicsPanel() {
    if (this.clinicsLoading) {
      return this.renderLoading();
    }

    return html`
      <div class="whitelist-section">
        <div class="whitelist-header">
          <div>
            <h3 style="margin: 0;">Clinic Groups</h3>
            <p style="color: #6c757d; margin: 0.5rem 0 0 0;">
              Organize clinicians by their clinic or practice
            </p>
          </div>
        </div>
      </div>

      <h4>Add Clinic Group</h4>
      <div class="whitelist-add-form">
        <sl-input
          placeholder="Clinic name"
          .value=${this.newClinicName}
          @sl-input=${(e: Event) =>
            (this.newClinicName = (e.target as HTMLInputElement).value)}
        >
          <sl-icon slot="prefix" name="hospital"></sl-icon>
        </sl-input>
        <sl-input
          placeholder="Description (optional)"
          .value=${this.newClinicDescription}
          @sl-input=${(e: Event) =>
            (this.newClinicDescription = (e.target as HTMLInputElement).value)}
        >
          <sl-icon slot="prefix" name="card-text"></sl-icon>
        </sl-input>
        <sl-button variant="primary" @click=${() => this.addClinicGroup()}>
          <sl-icon slot="prefix" name="plus"></sl-icon>
          Add
        </sl-button>
      </div>

      ${this.renderClinicsTable()}
    `;
  }

  private renderClinicsTable() {
    if (this.clinicGroups.length === 0) {
      return html`
        <div class="empty-state">
          <p>No clinic groups created yet</p>
        </div>
      `;
    }

    return html`
      <table class="whitelist-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${this.clinicGroups.map(
            (clinic) => html`
              <tr>
                <td style="font-weight: 500;">${clinic.name}</td>
                <td>${clinic.description || "-"}</td>
                <td>
                  <sl-badge variant=${clinic.isActive ? "success" : "neutral"}>
                    ${clinic.isActive ? "Active" : "Inactive"}
                  </sl-badge>
                </td>
                <td>${new Date(clinic.createdAt).toLocaleDateString()}</td>
                <td class="actions">
                  <sl-button
                    size="small"
                    variant="danger"
                    @click=${() => this.deleteClinicGroup(clinic.id)}
                  >
                    <sl-icon name="trash"></sl-icon>
                  </sl-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  private async addClinicGroup() {
    if (!this.newClinicName) return;

    try {
      await adminService.createClinicGroup(
        this.newClinicName,
        this.newClinicDescription || undefined,
      );
      this.newClinicName = "";
      this.newClinicDescription = "";
      await this.loadClinicGroups();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to create clinic group";
    }
  }

  private async deleteClinicGroup(id: string) {
    if (!confirm("Delete this clinic group?")) return;

    try {
      await adminService.deleteClinicGroup(id);
      await this.loadClinicGroups();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to delete clinic group";
    }
  }

  // =========================================================================
  // Whitelist Panel
  // =========================================================================

  private renderWhitelistPanel() {
    if (this.whitelistLoading) {
      return this.renderLoading();
    }

    return html`
      <div class="whitelist-section">
        <div class="whitelist-header">
          <div>
            <h3 style="margin: 0;">Registration Whitelist</h3>
            <p style="color: #6c757d; margin: 0.5rem 0 0 0;">
              When enabled, only whitelisted emails can register
            </p>
          </div>
          <sl-switch
            ?checked=${this.whitelist?.enabled ?? false}
            @sl-change=${(e: Event) =>
              this.toggleWhitelistEnabled(
                (e.target as HTMLInputElement).checked,
              )}
          >
            ${this.whitelist?.enabled ? "Enabled" : "Disabled"}
          </sl-switch>
        </div>
      </div>

      <h4>Add Email to Whitelist</h4>
      <div class="whitelist-add-form">
        <sl-input
          placeholder="Email address"
          type="email"
          .value=${this.newWhitelistEmail}
          @sl-input=${(e: Event) =>
            (this.newWhitelistEmail = (e.target as HTMLInputElement).value)}
        >
          <sl-icon slot="prefix" name="envelope"></sl-icon>
        </sl-input>
        <sl-input
          placeholder="Note (optional)"
          .value=${this.newWhitelistNote}
          @sl-input=${(e: Event) =>
            (this.newWhitelistNote = (e.target as HTMLInputElement).value)}
        >
          <sl-icon slot="prefix" name="sticky"></sl-icon>
        </sl-input>
      </div>
      <div class="whitelist-add-form" style="margin-top: -0.5rem;">
        <sl-select
          placeholder="Assign to clinic (optional)"
          clearable
          .value=${this.newWhitelistClinicGroupId}
          @sl-change=${(e: Event) =>
            (this.newWhitelistClinicGroupId = (
              e.target as HTMLSelectElement
            ).value)}
        >
          <sl-icon slot="prefix" name="hospital"></sl-icon>
          ${this.clinicGroups
            .filter((c) => c.isActive)
            .map(
              (clinic) => html`
                <sl-option value=${clinic.id}>${clinic.name}</sl-option>
              `,
            )}
        </sl-select>
        <sl-select
          placeholder="Pre-assign role (optional)"
          clearable
          .value=${this.newWhitelistRole}
          @sl-change=${(e: Event) =>
            (this.newWhitelistRole = (e.target as HTMLSelectElement).value)}
        >
          <sl-icon slot="prefix" name="person-badge"></sl-icon>
          <sl-option value="diabetic">Diabetic</sl-option>
          <sl-option value="monitor">Monitor</sl-option>
          <sl-option value="clinician">Clinician</sl-option>
        </sl-select>
        <sl-button variant="primary" @click=${() => this.addWhitelistEmail()}>
          <sl-icon slot="prefix" name="plus"></sl-icon>
          Add
        </sl-button>
      </div>

      ${this.renderWhitelistTable()}
    `;
  }

  private renderWhitelistTable() {
    const emails = this.whitelist?.emails ?? [];

    if (emails.length === 0) {
      return html`
        <div class="empty-state">
          <p>No whitelisted emails yet</p>
        </div>
      `;
    }

    // Helper to get clinic name by ID
    const getClinicName = (clinicId?: string) => {
      if (!clinicId) return "-";
      const clinic = this.clinicGroups.find((c) => c.id === clinicId);
      return clinic?.name || clinicId;
    };

    return html`
      <table class="whitelist-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Clinic</th>
            <th>Role</th>
            <th>Note</th>
            <th>Added By</th>
            <th>Added</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${emails.map(
            (entry) => html`
              <tr>
                <td>${entry.email}</td>
                <td>${getClinicName(entry.clinicGroupId)}</td>
                <td>
                  ${entry.preAssignedRole
                    ? html`<sl-badge variant="primary"
                        >${entry.preAssignedRole}</sl-badge
                      >`
                    : "-"}
                </td>
                <td>${entry.note || "-"}</td>
                <td>${entry.addedBy || "-"}</td>
                <td>${new Date(entry.addedAt).toLocaleDateString()}</td>
                <td>
                  <sl-button
                    size="small"
                    variant="danger"
                    @click=${() => this.removeWhitelistEmail(entry.email)}
                  >
                    <sl-icon name="trash"></sl-icon>
                  </sl-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  private async toggleWhitelistEnabled(enabled: boolean) {
    try {
      await adminService.setWhitelistEnabled(enabled);
      await this.loadWhitelist();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to update whitelist";
    }
  }

  private async addWhitelistEmail() {
    if (!this.newWhitelistEmail) return;

    try {
      await adminService.addWhitelistEmail(
        this.newWhitelistEmail,
        this.newWhitelistNote || undefined,
        this.newWhitelistClinicGroupId || undefined,
        this.newWhitelistRole || undefined,
      );
      this.newWhitelistEmail = "";
      this.newWhitelistNote = "";
      this.newWhitelistClinicGroupId = "";
      this.newWhitelistRole = "";
      await this.loadWhitelist();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to add email to whitelist";
    }
  }

  private async removeWhitelistEmail(email: string) {
    if (!confirm(`Remove ${email} from whitelist?`)) return;

    try {
      await adminService.removeWhitelistEmail(email);
      await this.loadWhitelist();
    } catch (err) {
      this.error =
        err instanceof Error
          ? err.message
          : "Failed to remove email from whitelist";
    }
  }

  private toggleRole(role: string, checked: boolean) {
    const newRoles = new Set(this.editRoles);
    if (checked) {
      newRoles.add(role);
    } else {
      newRoles.delete(role);
    }
    this.editRoles = newRoles;
  }

  private async toggleAdmin(isAdmin: boolean) {
    if (!this.editingUser) return;

    try {
      await adminService.setAdminStatus(this.editingUser.userId, isAdmin);
      this.editingUser = { ...this.editingUser, isAdmin };
      await this.loadUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to update admin status";
    }
  }

  private async saveUser() {
    if (!this.editingUser) return;

    try {
      await adminService.setUserRoles(
        this.editingUser.userId,
        Array.from(this.editRoles),
      );
      this.editingUser = null;
      await this.loadUsers();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to save user";
    }
  }

  private async deleteUser(user: UserSummary) {
    if (!confirm(`Delete user ${user.displayName}?`)) return;

    try {
      await adminService.deleteUser(user.userId);
      await this.loadUsers();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to delete user";
    }
  }

  private async restoreUser(user: UserSummary) {
    try {
      await adminService.restoreUser(user.userId);
      await this.loadUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to restore user";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "admin-page": AdminPage;
  }
}
